import { applyShareCiphertext, createShareCiphertext, readShareState, updateShareCursor } from './share.js';
import { fetchShareFeed, pushShareFeed, remoteConfigured } from './remote.js';

export type HouseholdSyncStatus = { state: 'idle' | 'syncing' | 'ready' | 'failed' | 'offline'; checkedAt?: number; message?: string };
const PENDING_KEY = 'petrhythm.household-pending';
type Pending = { petId: string; token: string; revision: number };
let status: HouseholdSyncStatus = { state: 'idle' };
const listeners = new Set<(value: HouseholdSyncStatus) => void>();
let queue: Promise<void> = Promise.resolve();
function update(next: HouseholdSyncStatus) { status = next; for (const listener of listeners) listener(status); }
function readPending(): Pending | null { try { return JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); } catch { return null; } }
export function getHouseholdSyncStatus() { return status; }
export function subscribeHouseholdSync(listener: (value: HouseholdSyncStatus) => void) { listeners.add(listener); listener(status); return () => { listeners.delete(listener); }; }

/** The local outbox survives offline and reload. Only encrypted snapshots leave the device. */
export function syncHousehold(upload = false): Promise<void> {
  const state = readShareState(); if (!state || !remoteConfigured()) return Promise.resolve();
  if (upload) {
    try { localStorage.setItem(PENDING_KEY, JSON.stringify({ petId: state.petId, token: state.token, revision: Math.max(Date.now(), (readPending()?.revision || 0) + 1) })); }
    catch { update({ ...status, state: 'failed', message: '재전송 대기를 보관하지 못했어요. 기기 기록은 남아 있으니 연결 뒤 지금 동기화를 눌러 주세요.' }); return Promise.resolve(); }
  }
  const stillConnected = () => { const value = readShareState(); return value?.petId === state.petId && value.token === state.token; };
  const task = queue.then(async () => {
    const current = readShareState(); if (!current || !stillConnected()) return;
    if (!navigator.onLine) { update({ ...status, state: 'offline', message: '기기 기록은 저장됐어요. 연결되면 대기 중인 기록을 가족에게 보내요.' }); return; }
    update({ ...status, state: 'syncing', message: undefined });
    let imported = 0;
    try {
      const pending = readPending();
      if (pending?.petId === current.petId && pending.token === current.token) {
        const ciphertext = await createShareCiphertext(current.petId, current.token);
        if (!stillConnected()) return;
        const result = await pushShareFeed(current.petId, ciphertext);
        if (!stillConnected()) return;
        if (!result) throw new Error('가족에게 기록을 보내지 못했어요. 기기 기록과 재전송 대기는 남아 있어요.');
        if (readPending()?.revision === pending.revision) localStorage.removeItem(PENDING_KEY);
      }
      let cursor = current.cursor;
      for (let page = 0; page < 5; page += 1) {
        const feed = await fetchShareFeed(current.petId, cursor);
        if (!stillConnected()) return;
        if (!feed) throw new Error('가족 기록을 확인하지 못했어요.');
        for (const item of feed.items) {
          if (!stillConnected()) return;
          const result = await applyShareCiphertext(item.ciphertext, current.token, item.actorLabel, current.petId, stillConnected);
          imported += result.imported;
          if (result.conflicts || result.failed) throw new Error('서로 다른 원본이 있어요. 기존 기록을 유지했으니 가족 기록 연결에서 확인해 주세요.');
        }
        if (!stillConnected()) return;
        if (feed.cursor > cursor) { cursor = feed.cursor; updateShareCursor(cursor); }
        if (!feed.hasMore) { update({ state: 'ready', checkedAt: Date.now() }); return; }
      }
      throw new Error('아직 더 확인할 가족 기록이 있어요. 다시 확인해 주세요.');
    } catch (cause) { if (stillConnected()) update({ ...status, state: 'failed', message: cause instanceof Error ? cause.message : '가족 기록 연결을 확인하지 못했어요.' }); }
    finally { if (imported && stillConnected()) window.dispatchEvent(new CustomEvent('petrhythm:household-applied')); }
  });
  queue = task.catch(() => undefined); return task;
}
